import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getSalatOrders } from "../../api";
import ConvertToArabicNumbers from "../../utils/convert_numbers";
import empty_box from "../../images/empty_box.png";

const STATUS_COLORS = {
  Pending: "bg-yellow-100 text-yellow-700",
  Confirmed: "bg-blue-100 text-blue-700",
  Processing: "bg-blue-100 text-blue-700",
  Ready: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  return (
    <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}>
      {t(`salatMahsool.status.${status}`)}
    </span>
  );
};

const SalatOrders = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { data: orders, isFetching } = useQuery({
    queryKey: ["salatOrders"],
    queryFn: () => getSalatOrders(),
    initialData: [],
  });

  const formatMoney = (value) => {
    const formatted = value.toFixed(2);
    return isRtl ? ConvertToArabicNumbers(formatted) : formatted;
  };

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="w-full lg:px-20 relative">
      <h1 className="font-bold text-2xl lg:text-3xl my-10 text-primary">{t("salatMahsool.orders.title")}</h1>

      {isFetching ? (
        <div className="my-16 grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 bg-gray-100 border rounded-xl shadow-sm animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : orders?.length === 0 ? (
        <div className="flex flex-col items-center mt-10 text-center">
          <img src={empty_box} alt="" className="w-40 lg:w-48" />
          <div className="font-bold mt-3 text-lg text-gray-700">{t("salatMahsool.orders.empty")}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {orders.map((order) => (
            <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Link
                to={`/home/profile/salat-orders/${order.id}`}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm transition-all hover:shadow-md hover:scale-[1.02]"
              >
                <div>
                  <div className="font-bold text-lg text-gray-800">
                    {t("salatMahsool.confirmation.orderNumber")} #{order.id}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("salatMahsool.orders.items")}: {order.items?.length ?? 0} |{" "}
                    {t("salatMahsool.orders.total")}: {formatMoney(parseFloat(order.total_amount))} {t("common.currency")}
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(order.created_at).toLocaleDateString(isRtl ? "ar-EG" : "en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
                <StatusBadge status={order.status} />
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SalatOrders;
export { StatusBadge };
